/**
 * Enables a Swiper-powered main image slider for the product media gallery
 * when the "Thumbnail carousel" gallery layout is selected. Non-destructive:
 * layers on top of Dawn's existing media-gallery markup/behavior so the
 * lightbox, zoom, XR button and thumbnail sync keep working unchanged.
 */
(function () {
  function getSlides(viewer) {
    var list = viewer.querySelector('.product__media-list');
    if (!list) return [];
    return Array.prototype.filter.call(list.children, function (el) {
      return el.matches && el.matches('li[data-media-id]');
    });
  }

  function initSwiperGallery(viewer) {
    if (!viewer || viewer.dataset.swiperInitialized === 'true') return;
    if (typeof Swiper === 'undefined') return;

    var slides = getSlides(viewer);
    if (!slides.length) return;

    var activeIndex = slides.findIndex(function (slide) {
      return slide.classList.contains('is-active');
    });
    if (activeIndex < 0) activeIndex = 0;

    var mediaGallery = viewer.closest('media-gallery');
    var counterCurrent = mediaGallery ? mediaGallery.querySelector('.slider-counter--current') : null;
    var dotsEl = mediaGallery ? mediaGallery.querySelector('.gallery-nav__dots'): null;

      var swiper = new Swiper(viewer, {
        slidesPerView: 1,
        spaceBetween: 1,
        watchOverflow: true,
        autoHeight: true,
        grabCursor: true,
        initialSlide: activeIndex,
        a11y: false,
        pagination: dotsEl
          ? {
              el: dotsEl,
              clickable: true,
              bulletClass: 'gallery-nav__dot',
              bulletActiveClass: 'is-active',
              bulletElement: 'button',
              renderBullet: function (index, className) {
                return '<button type="button" class="' + className + '" aria-label="Load slide ' + (index + 1) + '"></button>';
              },
            }
          : false,
        on: {
          slideChange: function (instance) {
            var activeSlide = slides[instance.activeIndex];
            if (mediaGallery && activeSlide && typeof mediaGallery.setActiveMedia === 'function') {
              mediaGallery.setActiveMedia(activeSlide.dataset.mediaId, false);
            } else {
              slides.forEach(function (slide, index) {
                slide.classList.toggle('is-active', index === instance.activeIndex);
              });
            }
            if (counterCurrent) counterCurrent.textContent = instance.activeIndex + 1;
          },
        },
      });

    viewer.dataset.swiperInitialized = 'true';
    viewer.swiperInstance = swiper;

    // Keep Swiper in sync when something else (thumbnail click, variant AJAX
    // update via media-gallery.js#setActiveMedia) toggles the is-active class.
    var observer = new MutationObserver(function (mutations) {
      mutations.forEach(function (mutation) {
        var slide = mutation.target;
        if (!slide.classList.contains('is-active')) return;
        var index = slides.indexOf(slide);
        if (index > -1 && swiper.activeIndex !== index) swiper.slideTo(index);
      });
    });
    slides.forEach(function (slide) {
      observer.observe(slide, { attributes: true, attributeFilter: ['class'] });
    });

    // Wire up Dawn's existing prev/next buttons (rendered inside the viewer,
    // overlaid via the .slider-buttons--overlay CSS modifier).
    var buttons = viewer.querySelector('.slider-buttons');
    if (buttons) {
      var prev = buttons.querySelector('.slider-button--prev');
      var next = buttons.querySelector('.slider-button--next');
      if (prev) prev.addEventListener('click', function () { swiper.slidePrev(); });
      if (next) next.addEventListener('click', function () { swiper.slideNext(); });
    }
  }

  function getThumbSlides(thumbsEl) {
    var list = thumbsEl.querySelector('.thumbnail-list');
    if (!list) return [];
    return Array.prototype.filter.call(list.children, function (el) {
      return el.matches && el.matches('li[data-target]');
    });
  }

  function initSwiperThumbs(thumbsEl) {
    if (!thumbsEl || thumbsEl.dataset.swiperInitialized === 'true') return;
    if (typeof Swiper === 'undefined') return;

    var slides = getThumbSlides(thumbsEl);
    if (!slides.length) return;

    var activeIndex = slides.findIndex(function (slide) {
      var button = slide.querySelector('button');
      return button && button.getAttribute('aria-current') === 'true';
    });
    if (activeIndex < 0) activeIndex = 0;

    var swiper = new Swiper(thumbsEl, {
      slidesPerView: 3,
      spaceBetween: 16,
      watchOverflow: true,
      grabCursor: true,
      initialSlide: activeIndex,
      a11y: false,
      breakpoints: {
        750: {
          slidesPerView: 6,
          spaceBetween: 16,
        },
      },
    });

    thumbsEl.dataset.swiperInitialized = 'true';
    thumbsEl.swiperInstance = swiper;

    // Dawn's media-gallery.js drives the actual media switch via the
    // existing click listeners on each thumbnail button; here we only need
    // Swiper to keep the active thumbnail scrolled into view (see the
    // setActiveThumbnail patch in media-gallery.js) and to wire the
    // pre-existing prev/next buttons.
    var prev = thumbsEl.querySelector('.slider-button--prev');
    var next = thumbsEl.querySelector('.slider-button--next');
    var step = parseInt((next && next.dataset.step) || (prev && prev.dataset.step), 10) || 1;
    if (prev) {
      prev.addEventListener('click', function () {
        swiper.slideTo(Math.max(0, swiper.activeIndex - step));
      });
    }
    if (next) {
      next.addEventListener('click', function () {
        swiper.slideTo(Math.min(slides.length - 1, swiper.activeIndex + step));
      });
    }
  }

  function initAll(root) {
    (root || document).querySelectorAll('media-gallery[data-swiper-gallery] [data-swiper-viewer]').forEach(initSwiperGallery);
    (root || document).querySelectorAll('media-gallery[data-swiper-gallery] [data-swiper-thumbs]').forEach(initSwiperThumbs);
  }

  if (document.readyState !== 'loading') {
    initAll(document);
  } else {
    document.addEventListener('DOMContentLoaded', function () {
      initAll(document);
    });
  }

  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.variantChange, function (event) {
      var sectionId = event && event.data && event.data.sectionId;
      var variant = event && event.data && event.data.variant;
      if (!sectionId || !variant || !variant.featured_media) return;

      var mediaGallery = document.querySelector(
        'media-gallery[data-swiper-gallery][data-section-id="' + sectionId + '"]'
      );
      var viewer = mediaGallery && mediaGallery.querySelector('[data-swiper-viewer]');
      if (!viewer || !viewer.swiperInstance) return;

      var mediaId = sectionId + '-' + variant.featured_media.id;
      var slides = getSlides(viewer);
      var index = slides.findIndex(function (slide) {
        return slide.dataset.mediaId === mediaId;
      });
      if (index > -1) viewer.swiperInstance.slideTo(index);
    });
  }
})();
